import {
  MoneyInput,
  SubmitPayOverTimeManualPaymentUnion,
  SubmitPayOverTimeManualPaymentUnionTypenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  buildErrorModel,
  logPayOverTimeError,
  type PayOverTimeErrorConfig,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useState } from 'react';
import SubmitPayOverTimeManualPayment from 'services/graphql/mutations/SubmitPayOverTimeManualPayment';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';

const SUBMIT_OPERATION = 'submitPayOverTimeManualPayment';

const useSubmitPayOverTimeManualPayment = () => {
  const router = useRouter();
  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();
  const [{ data, fetching, error }, executeMutation] = useCancellableMutation<{
    submitPayOverTimeManualPayment: SubmitPayOverTimeManualPaymentUnion;
  }>(SubmitPayOverTimeManualPayment);
  const { idempotencyToken } = useIdempotencyToken('0');
  const [localErrorModel, setLocalErrorModel] =
    useState<PayOverTimeErrorConfig | null>(null);

  const buildErrorModelWithButton = (
    headline: string,
    body: string,
  ): PayOverTimeErrorConfig => ({
    ...buildErrorModel(headline, body),
    primaryButton: {
      title: cancelButtonTitle,
      onPress: router.back,
    },
  });

  const networkErrorModel: PayOverTimeErrorConfig | null = error
    ? buildErrorModelWithButton(defaultTitle, defaultMessage)
    : null;

  const resultVariant = data?.submitPayOverTimeManualPayment;
  const successVariant =
    resultVariant?.__typename ===
    SubmitPayOverTimeManualPaymentUnionTypenames.PayOverTimeSubmitManualPaymentSuccess
      ? resultVariant
      : null;
  const graphqlErrorVariant: PayOverTimeErrorConfig | null =
    resultVariant?.__typename ===
    SubmitPayOverTimeManualPaymentUnionTypenames.PayOverTimeSubmitManualPaymentError
      ? buildErrorModelWithButton(resultVariant.title, resultVariant.message)
      : null;

  const executeMutationSubmitPayOverTimeManualPayment = async (input: {
    loanId: string;
    amount: MoneyInput;
    fromAccountId: string;
  }) => {
    const variables = {
      idempotencyId: idempotencyToken,
      input: {
        loanId: input.loanId,
        sourceAccountId: input.fromAccountId,
        amount: input.amount,
      },
    };

    setLocalErrorModel(null);
    try {
      const response = await executeMutation(variables);
      const variant = response.data?.submitPayOverTimeManualPayment;

      if (
        variant?.__typename ===
        SubmitPayOverTimeManualPaymentUnionTypenames.PayOverTimeSubmitManualPaymentError
      ) {
        logPayOverTimeError(new Error(variant.message), {
          loanId: input.loanId,
          code: variant.code,
          typename: variant.__typename,
          operation: SUBMIT_OPERATION,
        });
      } else if (response.error) {
        logPayOverTimeError(response.error, {
          loanId: input.loanId,
          operation: SUBMIT_OPERATION,
        });
      }

      return variant;
    } catch (caught) {
      if (caught instanceof AbortError) {
        return null;
      }

      logPayOverTimeError(caught, {
        loanId: input.loanId,
        operation: SUBMIT_OPERATION,
      });
      setLocalErrorModel(
        buildErrorModelWithButton(defaultTitle, defaultMessage),
      );
      return null;
    }
  };

  const errorModel =
    networkErrorModel || graphqlErrorVariant || localErrorModel;

  return {
    executeMutationSubmitPayOverTimeManualPayment,
    fetching,
    data: successVariant,
    errorModel,
  };
};

export default useSubmitPayOverTimeManualPayment;
